import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Clock, Headphones, Lock, Loader2 } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";

const LOGO_URL = "/images/logo-full.png";
const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function TrialExpired() {
  const { user, token, logout } = useAuth();
  const navigate = useNavigate();
  const [upgrading, setUpgrading] = useState(false);

  useEffect(() => {
    if (user?.account_status === "trial_active") {
      navigate("/upgrade", { replace: true });
    }
  }, [user?.account_status, navigate]);

  const handleUpgrade = async () => {
    if (!token) {
      window.location.href = "/register";
      return;
    }
    setUpgrading(true);
    try {
      const { data } = await axios.post(
        `${API_URL}/api/billing/trial/upgrade-checkout`,
        { plan: "monthly", plan_tier: user?.plan_tier || "tier_1" },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (data.requiresStripeRedirect === false && data.checkoutUrl) {
        window.location.href = data.checkoutUrl;
      } else if (data.checkoutUrl) {
        window.location.href = data.checkoutUrl;
      } else {
        toast.error("Impossibile avviare il checkout. Riprova o contatta l'assistenza.");
      }
    } catch (error) {
      console.error("Upgrade checkout error:", error);
      toast.error(error.response?.data?.detail || "Errore durante l'avvio dell'upgrade");
    } finally {
      setUpgrading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#f8fbfa] flex flex-col">
      <main className="flex-1 flex items-center justify-center px-4 py-10">
        <div className="w-full max-w-lg text-center">
          <img src={LOGO_URL} alt="Affidya" className="mx-auto mb-8 h-12" />

          <div className="relative mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-full bg-emerald-50">
            <Clock className="h-10 w-10 text-emerald-600" />
            <span className="absolute -right-0.5 -top-0.5 h-4 w-4 rounded-full bg-red-500 ring-2 ring-white" />
          </div>

          <h1 className="text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
            La tua prova gratuita è terminata
          </h1>
          <p className="mt-4 text-sm leading-6 text-slate-600">
            Hai utilizzato Affidya per 14 giorni. Per continuare ad accedere ai dati, ai fascicoli e agli strumenti della piattaforma, effettua l&apos;upgrade.
          </p>

          <div className="mt-8 space-y-3">
            <Button
              onClick={handleUpgrade}
              disabled={upgrading}
              className="h-12 w-full rounded-xl bg-emerald-600 text-base font-semibold hover:bg-emerald-700"
            >
              {upgrading ? (
                <span className="flex items-center gap-2">
                  <Loader2 className="h-5 w-5 animate-spin" />
                  Reindirizzamento...
                </span>
              ) : (
                "Effettua upgrade"
              )}
            </Button>
            <Button
              asChild
              variant="outline"
              className="h-12 w-full rounded-xl border-slate-200 text-base font-semibold text-emerald-700 hover:bg-emerald-50"
            >
              <a href="mailto:supporto@affidya.it?subject=Upgrade%20Affidya">
                <Headphones className="mr-2 h-5 w-5" />
                Contatta assistenza
              </a>
            </Button>
          </div>

          <div className="mt-8 flex items-start gap-3 rounded-xl border border-sky-100 bg-sky-50/80 p-4 text-left">
            <Lock className="mt-0.5 h-5 w-5 shrink-0 text-sky-600" />
            <p className="text-sm text-slate-700">
              I tuoi dati restano salvati. Potrai recuperarli dopo l&apos;attivazione del piano.
            </p>
          </div>

          <button
            type="button"
            onClick={logout}
            className="mt-6 text-sm text-slate-500 hover:text-slate-700 hover:underline"
          >
            Esci dall&apos;account
          </button>
        </div>
      </main>

      <footer className="py-6 text-center">
        <div className="flex justify-center gap-5 text-xs text-slate-500">
          <Link to="/privacy" className="hover:text-slate-700">Privacy Policy</Link>
          <Link to="/cookies" className="hover:text-slate-700">Cookie Policy</Link>
          <Link to="/terms" className="hover:text-slate-700">Termini di Servizio</Link>
        </div>
        <p className="mt-3 text-xs text-slate-400">
          © {new Date().getFullYear()} Affidya S.r.l.
        </p>
      </footer>
    </div>
  );
}
